<template>
  <footer v-if="total > 0" class="pl-pagination" aria-label="List pagination">
    <div class="pl-pagination__summary">
      <span class="pl-pagination__range">{{ rangeLabel }}</span>
      <span class="pl-pagination__pages">Page {{ page }} of {{ pageCount }}</span>
    </div>

    <div class="pl-pagination__sizes" role="group" aria-label="Rows per page">
      <span class="pl-pagination__sizes-label">Show</span>
      <button
        v-for="option in perPageOptions"
        :key="option.value"
        type="button"
        class="pl-pagination__size"
        :class="{ 'pl-pagination__size--active': itemsPerPage === option.value }"
        :aria-pressed="itemsPerPage === option.value"
        @click="$emit('update:itemsPerPage', option.value)"
      >
        {{ option.label }}
      </button>
    </div>

    <nav class="pl-pagination__nav" aria-label="Page navigation">
      <button
        type="button"
        class="pl-pagination__nav-btn"
        :disabled="page <= 1"
        aria-label="Previous page"
        @click="$emit('update:page', page - 1)"
      >
        <v-icon icon="mdi-chevron-left" size="18" />
      </button>

      <template v-for="(item, index) in pageItems" :key="`${item}-${index}`">
        <span v-if="item === 'ellipsis'" class="pl-pagination__ellipsis" aria-hidden="true">…</span>
        <button
          v-else
          type="button"
          class="pl-pagination__page"
          :class="{ 'pl-pagination__page--active': item === page }"
          :aria-current="item === page ? 'page' : undefined"
          @click="$emit('update:page', item)"
        >
          {{ item }}
        </button>
      </template>

      <button
        type="button"
        class="pl-pagination__nav-btn"
        :disabled="page >= pageCount"
        aria-label="Next page"
        @click="$emit('update:page', page + 1)"
      >
        <v-icon icon="mdi-chevron-right" size="18" />
      </button>
    </nav>
  </footer>
</template>

<script setup>
import { computed } from 'vue'

defineOptions({ name: 'PortalListPagination' })

const props = defineProps({
  page: { type: Number, default: 1 },
  pageCount: { type: Number, default: 1 },
  itemsPerPage: { type: Number, default: 25 },
  rangeLabel: { type: String, default: '' },
  total: { type: Number, default: 0 },
})

defineEmits(['update:page', 'update:itemsPerPage'])

const perPageOptions = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: 'All', value: -1 },
]

function buildPageItems(current, total) {
  if (total <= 1) return total === 1 ? [1] : []
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1)
  }

  const items = []
  const add = (value) => items.push(value)
  const addEllipsis = () => {
    if (items[items.length - 1] !== 'ellipsis') add('ellipsis')
  }

  add(1)

  if (current > 3) addEllipsis()

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  for (let pageNumber = start; pageNumber <= end; pageNumber += 1) {
    add(pageNumber)
  }

  if (current < total - 2) addEllipsis()

  add(total)
  return items
}

const pageItems = computed(() => buildPageItems(props.page, props.pageCount))
</script>
